import { extractApiErrorMessage, extractValidationErrors } from '../../../Utils/apiErrorUtils';
import { EntityFormApiErrorExtractionResult } from '../../Shared/Hooks/useEntityForm.types';
import { PartiesErrorFeedback, PartyFormValues } from '../Types/partyTypes';

const PARTY_ERROR_MESSAGES_BY_CODE: Record<string, string> = {
  PARTY_CONFLICT_DUPLICATE:
    'Istnieje już aktywna organizacja z tym samym adresem (ulica i miasto). Zweryfikuj ulicę i miasto.',
  PARTY_INACTIVE_UPDATE_BLOCKED:
    'Nie można edytować nieaktywnej strony. Najpierw przywróć aktywność albo utwórz nową stronę.',
  PARTY_TYPE_CHANGE_NOT_ALLOWED:
    'Nie można zmienić typu strony. Utwórz nową stronę z poprawnym typem.',
};

const normalizeApiFieldPath = (fieldPath: string): string =>
  fieldPath
    .replace(/^\$?\./, '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((segment) => {
      if (!segment) {
        return segment;
      }

      return segment.charAt(0).toLowerCase() + segment.slice(1);
    })
    .join('.');

const getErrorCode = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return null;
  }

  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('errorCode' in data)) {
    return null;
  }

  const errorCode = (data as { errorCode?: unknown }).errorCode;
  if (typeof errorCode !== 'string' || !errorCode.trim()) {
    return null;
  }

  return errorCode.trim();
};

export const extractPartiesErrorFeedback = (
  error: unknown,
  fallbackMessage: string
): PartiesErrorFeedback => {
  const errorCode = getErrorCode(error);

  if (errorCode && PARTY_ERROR_MESSAGES_BY_CODE[errorCode]) {
    return {
      errorCode,
      message: PARTY_ERROR_MESSAGES_BY_CODE[errorCode],
    };
  }

  return {
    errorCode,
    message: extractApiErrorMessage(error, fallbackMessage),
  };
};

export const extractPartyEntityFormApiErrors = (
  error: unknown,
  fallbackMessage: string
): EntityFormApiErrorExtractionResult => {
  const validationErrors = extractValidationErrors(error);
  const fieldErrors: Record<string, string> = {};

  if (validationErrors) {
    Object.entries(validationErrors).forEach(([fieldName, messages]) => {
      const firstMessage = Array.isArray(messages) ? messages[0] : undefined;
      if (!firstMessage) {
        return;
      }

      fieldErrors[normalizeApiFieldPath(fieldName)] = firstMessage;
    });
  }

  const feedback = extractPartiesErrorFeedback(error, fallbackMessage);
  return {
    submitErrorMessage: feedback.message,
    fieldErrors,
  };
};

export const validatePartyFormValues = (
  values: PartyFormValues
): Record<string, string[]> | null => {
  const errors: Record<string, string[]> = {};
  const isOrganization = values.partyType === 'organization';
  const email = values.email.trim();

  if (isOrganization) {
    if (!values.organizationName.trim()) {
      errors.organizationName = ['Nazwa organizacji jest wymagana.'];
    }

    if (!values.taxId.trim()) {
      errors.taxId = ['NIP jest wymagany dla organizacji.'];
    }
  } else {
    if (!values.firstName.trim()) {
      errors.firstName = ['Imię jest wymagane dla osoby fizycznej.'];
    }

    if (!values.lastName.trim()) {
      errors.lastName = ['Nazwisko jest wymagane dla osoby fizycznej.'];
    }
  }

  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = ['Wprowadź poprawny adres e-mail.'];
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

